import { useMsal } from '@azure/msal-react';
import { Separator } from '@fluentui/react';
import { RouterParams } from 'App';
import { RootState } from 'app/store';
import SpinnerNotification, { ESpinnerVariant } from 'components/controls/Notification/SpinnerNotification';
import EditorNavbar from 'components/layout/EditorNavbar';
import { compareUnderWritingReviewTo, EViewType } from 'global/Application';
import { appInsights } from 'global/Application/AppInsight';
import { ESaveType, ISaveInstruction, loadSnapshotAction, SaveAction, SaveAsync } from "global/Application/dataActions";
import { MsalTokenSource } from 'global/DataSources/MsalTokenSource';
import PageLoading from 'global/PageLoading';
import { updateIteration } from 'global/Reducer/dataSlice';
import { ELoadStatus, ESnapshotStatus, setViewType, updateCreatingStatus } from 'global/Reducer/statusSlice';
import { trimGUIDLower } from 'global/Utility/TSFunctions';
import { IComponentContextMenu } from 'model/Application';
import { bcbs_groupconfigurationsnapshot } from 'model/AttributeOptionSets.type';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { AppDataSource } from '../../global/DataSources/AppDataSource';
import EntryDialog from './EntryDialog';
import GroupConfigurationProperties from './GroupConfigurationProperties';
import RelationshipInformation from './RelationshipInformation';

interface IProps extends IComponentContextMenu {
    children: React.ReactNode;
    isChecklist?: boolean;
}

// TODO rename this file to GroupConfigurationSnapshotContext
const GroupConfigurationSnapshotContext = (props: IProps) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { id: accountId, snapshotId } = useParams<RouterParams>();
    const state = useSelector((state: RootState) => state);
    const { instance } = useMsal();

    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    useEffect(() => {

        if (accountId && snapshotId) {
            let reload = false;

            if (!reload)
                if (!state.status.snapshotLoading)
                    if (!state?.data?.snapshot?.bcbs_groupconfigurationsnapshotid)
                        reload = true;

            if (!reload)
                if (state.status.account &&
                    state.data?.snapshot?.bcbs_groupconfigurationsnapshotid &&
                    trimGUIDLower(state.data.snapshot.bcbs_groupconfigurationsnapshotid!) !== trimGUIDLower(snapshotId)
                )
                    reload = true;

            if (!reload)
                if (state.status.account &&
                    state.data.snapshot?.bcbs_groupconfigurationsnapshotid &&
                    trimGUIDLower(state.status.account?.accountid!) !== trimGUIDLower(accountId)
                )
                    reload = true;

            if (reload) {

                appInsights?.trackEvent({
                    name: 'BlueRelationshipContext.useEffect.loadSnapshotAction',
                    properties: {
                        snapshotId: snapshotId,
                        accountId: accountId,
                        stateSnapshotId: state?.data?.snapshot?.bcbs_groupconfigurationsnapshotid,
                        stateAccountId: state?.data?.account?.accountid,
                    }
                });

                dispatch(loadSnapshotAction(
                    dataSource,
                    accountId!,
                    {
                        primarySnapshotId: snapshotId,
                        purgeStore: true
                    }
                ));
            }
        }
        else {
            console.error("Unexpected flow, snapshotId or accountId was expected.")
        }
    }, [accountId, snapshotId]);

    function onRenderDialogs() {
        if (state.status.viewType === null)
            return <EntryDialog
                variant='default'
                isChecklist={props.isChecklist}
                onConfirm={async (action) => {

                    appInsights?.trackEvent({
                        name: 'BlueRelationshipContext.EntryDialog.onConfirm',
                        properties: {
                            action: action
                        }
                    });

                    if (action === "view") {
                        dispatch(setViewType(EViewType.ViewOnly));

                    } else if (action === "modify") {
                        dispatch(setViewType(EViewType.Modify));
                        dispatch(updateIteration());

                    } else if (action === "reopen and modify") {

                        await SaveAsync(dispatch, dataSource, state, {
                            onSaveSetStatusCode: bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value,
                            onSaveStatusScreen: true
                        });

                        dispatch(setViewType(EViewType.Modify));
                        dispatch(updateIteration());

                    } else if (action === "create") {
                        dispatch(setViewType(EViewType.Modify));
                        dispatch(updateCreatingStatus(true));
                        //TODO maybe better not to create a snapshot at this step but instead :
                        //flag a status for this condition to create a snapshot after global visibility has been setup, allowing the user to pick changetypes.
                    } else if (action === "underwriting review") {

                        if (!state?.data?.snapshot?.bcbs_groupconfigurationsnapshotid)
                            return;

                        dispatch(setViewType(EViewType.ViewOnly))

                        const foundSnapshot = await compareUnderWritingReviewTo(state, state.data.snapshot)(dispatch);

                        if (foundSnapshot) {
                            // if it has an originating snapshot
                            const link = `/underwritingReview/${accountId}/${trimGUIDLower(foundSnapshot.bcbs_groupconfigurationsnapshotid!)}/${trimGUIDLower(state.data.snapshot.bcbs_groupconfigurationsnapshotid)}`
                            history.push(link);
                        } else {
                            // has no originating snapshot, an initial setup case
                            const link = `/underwritingReview/${accountId}/${snapshotId}/`
                            history.push(link);
                        }

                        // TODO M.Ual, I think this is not needed
                        dispatch(updateIteration());

                    } else {
                        throw new Error("Unexpected action");
                    }
                }}
            />;

        // snapshot is missing some information ? or create has been exclusively requested?
        // Scenarios:
        // 1) initializing a snapshot, it is brand new snapshot, and has not been saved before, transformer created the fields, we issue a save first
        // 2) existing snapshot, we are creating new one off this, 
        // 3) we are just changing properties (this one cant happen here, review GroupConfiguration > GroupConfigurationProperties)

        if (state.data.json.fields.length === 0 || !state.data.json.effectiveDate || !state.data.json.changeType || state.status.creating) {

            return <GroupConfigurationProperties
                isOpen={true}
                variant='setup'
                defaultValue={state.data.json}
                onConfirm={(gcPropertiesArgs) => {

                    if (!gcPropertiesArgs)
                        return;

                    appInsights?.trackEvent({
                        name: 'BlueRelationshipContext.groupConfigurationProperties.onConfirm',
                        properties: {
                            value: gcPropertiesArgs.value
                        }
                    });

                    const initializing = state.status.snapshotStatus === ESnapshotStatus.untracked;

                    if (initializing) {
                        const instructions: ISaveInstruction = {
                            saveType: ESaveType.ECreate,
                            onSaveSetStatusCode: bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value,
                            onSaveStatusScreen: true,
                            onPreSaveUpdateJson: gcPropertiesArgs.value,
                        }

                        // just save the current new snapshot and carry on
                        dispatch(
                            SaveAction(
                                dataSource,
                                history,
                                instructions,
                            )
                        );

                    } else {

                        //makes a new snapshot with this snapshot as parent
                        const instructions: ISaveInstruction = {
                            onSaveClone: true,
                            onCloneSetOriginatingSnapshotId: state.data.snapshot.bcbs_groupconfigurationsnapshotid,
                            onSaveSetStatusCode: bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value,
                            onSaveStatusScreen: true,
                            onPostSaveNavigateExternalToSaveResult: true,
                            onPreSaveUpdateJson: {
                                ...gcPropertiesArgs.value,
                                instructions: {
                                    ...gcPropertiesArgs.value.instructions,

                                    // we don't want the visibility to run when creating a snapshot in future with a release that is currently associated.
                                    ignoreVisibilityRun: true,
                                }
                            },
                        }

                        // this is an existing snapshot, we are creating a new one off this one:
                        dispatch(
                            SaveAction(
                                dataSource,
                                history,
                                instructions,
                            )
                        );

                    }
                }}
            />
        }

        return null;
    }

    function onRenderChildren() {
        if (state.status.viewType === null)
            return <></>;

        return <div style={{ paddingLeft: window.innerWidth > 720 ? '48px' : '1px', paddingRight: window.innerWidth > 720 ? '48px' : '2px', marginTop: '10px' }}>
            {props.children}
        </div>;
    }

    function onRenderNotifications() {
        return <SpinnerNotification
            hidden={(state.status.dataSavingStatus === ELoadStatus.idle)}
            variant={
                state.status.dataSavingStatus === ELoadStatus.criticalSaving || state.status.dataSavingStatus === ELoadStatus.saving
                    ? ESpinnerVariant.spinner
                    : state.status.dataSavingStatus === ELoadStatus.complete
                        ? ESpinnerVariant.done
                        : ESpinnerVariant.default
            }
        />;
    }

    if (state.status.snapshotLoading)
        return <PageLoading />;

    if (!state?.data?.json?.fields)
        return <></>;

    return <>
        {onRenderNotifications()}
        <EditorNavbar key={"navbar"} contextMenu={props.contextMenu} />
        {onRenderDialogs()}
        <Separator />
        <RelationshipInformation />
        {onRenderChildren()}
        {state.status.processing &&
            <PageLoading />
        }
    </>;
}

export default GroupConfigurationSnapshotContext;

